import * as Cesium from "cesium";

// Returns array of { entity, id, reasons } for entities where at least one given key
// has an "empty" value (null, undefined, empty string, "null", "undefined", empty array/object)
export function getEntitiesWithEmptyValuesForKeys(dataSource, keyNames = []) {
  if (!dataSource || !dataSource.entities || !Array.isArray(keyNames) || keyNames.length === 0) {
    return [];
  }

  const requiredKeys = keyNames.map((k) => String(k).trim()).filter(Boolean);
  if (requiredKeys.length === 0) return [];

  function isEmptyValue(value) {
    if (value === null || value === undefined) return true;

    if (typeof value === "string") {
      const trimmed = value.trim().toLowerCase();
      if (trimmed === "") return true;
      if (trimmed === "null") return true;
      if (trimmed === "undefined") return true;
      return false;
    }

    if (Array.isArray(value) && value.length === 0) return true;

    if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return true;

    return false;
  }

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) {
    return [];
  }

  const result = [];
  const seenIds = new Set();

  for (const entity of entities) {
    if (!entity) continue;

    const props = entity.properties;
    if (!props) continue;

    const reasons = [];

    for (const key of requiredKeys) {
      const value = getPropertyValueFromProps(props, key);
      if (isEmptyValue(value)) {
        reasons.push(`Empty value for key: ${key}`);
      }
    }

    if (reasons.length > 0) {
      const id = getEntityId(entity);
      const dedupeKey = id ?? entity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({ entity, id, reasons });
      }
    }
  }

  return result;
}
